import {
  ReadableSpan,
  Span,
  SpanProcessor,
  WebTracerProvider,
} from '@opentelemetry/sdk-trace-web'
import { resourceFromAttributes } from '@opentelemetry/resources'
import {
  BatchSpanProcessor,
  Sampler,
  SamplingDecision,
} from '@opentelemetry/sdk-trace-base'
import { OTLPTraceExporter } from '@opentelemetry/exporter-trace-otlp-http'
import { registerInstrumentations } from '@opentelemetry/instrumentation'
import { FetchInstrumentation } from '@opentelemetry/instrumentation-fetch'
import { XMLHttpRequestInstrumentation } from '@opentelemetry/instrumentation-xml-http-request'
import { DocumentLoadInstrumentation } from '@opentelemetry/instrumentation-document-load'
import {
  SpanStatusCode,
  trace,
  context as otelContext,
} from '@opentelemetry/api'

const env = import.meta.env.VITE_BRANCH || 'unknown'
const serviceName = `${env}-livox-frontend`

const getFullUrlPath = () => {
  const path = window.location.pathname
  const hash = window.location.hash
  return hash && hash.startsWith('#') ? path + hash : path
}

const getUserData = () => {
  try {
    const userInfoStr = localStorage.getItem('userInfo')
    if (userInfoStr) {
      const userInfo = JSON.parse(userInfoStr)
      return {
        email: userInfo.email || userInfo.data?.email || 'anonymous',
        uuid: userInfo.uuid || userInfo.data?.uuid || 'anonymous',
      }
    }
  } catch (e) {}
  return { email: 'anonymous', uuid: 'anonymous' }
}

const initOTel = () => {
  try {
    console.log(`OTel: Initializing for service: ${serviceName}`)

    const formatSpanName = (span: ReadableSpan) => {
      const attributes = span.attributes
      const httpMethod = attributes['http.method']
      const httpUrl = attributes['http.url']
      if (httpMethod && httpUrl) {
        try {
          const url = new URL(String(httpUrl))
          return `${httpMethod} ${url.pathname}`
        } catch (e) {
          return `${httpMethod} ${httpUrl}`
        }
      }
      return null
    }

    const userAttributeProcessor: SpanProcessor = {
      onStart: (span: Span) => {
        const user = getUserData()
        const pagePath = getFullUrlPath()

        span.setAttribute('user.email', user.email)
        span.setAttribute('user.uuid', user.uuid)
        span.setAttribute('page.path', pagePath)
        span.setAttribute('service.name', serviceName)

        span.setStatus({ code: SpanStatusCode.OK })

        const newName = formatSpanName(span)
        if (newName) {
          span.updateName(newName)
        } else if (
          span.name === 'documentLoad' ||
          span.name === 'documentFetch'
        ) {
          span.updateName(`${span.name}: ${pagePath}`)
        }
      },
      onEnd: (span: ReadableSpan) => {
        const newName = formatSpanName(span)
        if (newName && !span.name.includes('/')) {
          ;(span as Span).updateName(newName)
        }
      },
      shutdown: () => Promise.resolve(),
      forceFlush: () => Promise.resolve(),
    }

    // Keeps page-load timing (documentLoad/documentFetch) but drops one span per JS/CSS/image asset.
    const dropResourceFetchSampler: Sampler = {
      shouldSample: (_context, _traceId, spanName) => ({
        decision:
          spanName === 'resourceFetch'
            ? SamplingDecision.NOT_RECORD
            : SamplingDecision.RECORD_AND_SAMPLED,
      }),
      toString: () => 'DropResourceFetchSampler',
    }

    const provider = new WebTracerProvider({
      resource: resourceFromAttributes({ 'service.name': serviceName }),
      sampler: dropResourceFetchSampler,
      spanProcessors: [
        userAttributeProcessor,
        new BatchSpanProcessor(
          new OTLPTraceExporter({ url: '/otlp-v1/v1/traces' }),
        ),
      ],
    })

    registerInstrumentations({
      instrumentations: [
        new DocumentLoadInstrumentation(),
        new FetchInstrumentation({
          ignoreUrls: [/.*\/otlp-v1\/.*/],
          applyCustomAttributesOnSpan: (span, _request, response) => {
            if (response instanceof Response) {
              if (response.status >= 400) {
                span.setStatus({
                  code: SpanStatusCode.ERROR,
                  message: `HTTP ${response.status}`,
                })
              } else {
                span.setStatus({ code: SpanStatusCode.OK })
              }
            }
          },
        }),
        new XMLHttpRequestInstrumentation({
          ignoreUrls: [/.*\/otlp-v1\/.*/],
          applyCustomAttributesOnSpan: (span, xhr) => {
            if (xhr.status >= 400) {
              span.setStatus({
                code: SpanStatusCode.ERROR,
                message: `HTTP ${xhr.status}`,
              })
            } else if (xhr.status > 0) {
              span.setStatus({ code: SpanStatusCode.OK })
            }
          },
        }),
      ],
      tracerProvider: provider,
    })

    provider.register()

    window.onerror = (message, _source, _lineno, _colno, error) => {
      captureError(error || new Error(String(message)))
    }

    window.onunhandledrejection = (event: PromiseRejectionEvent) => {
      const error =
        event.reason instanceof Error
          ? event.reason
          : new Error(String(event.reason))
      captureError(error)
    }
  } catch (error) {
    console.error('OTel: Failed to initialize tracing', error)
  }
}

export function reportError(
  error: Error,
  serviceName: string,
  pagePath: string,
  user: { email: string; uuid: string },
) {
  try {
    const tracer = trace.getTracer(serviceName)
    const span = tracer.startSpan('frontend.error', {
      attributes: {
        'error.type': error.name,
        'error.message': error.message,
        'error.stack': error.stack || '',
        'page.path': pagePath,
        'user.email': user.email,
        'user.uuid': user.uuid,
      },
    })
    span.setStatus({ code: SpanStatusCode.ERROR, message: error.message })

    const apiBase = import.meta.env.VITE_SERVER_URL_BASE || ''
    otelContext.with(trace.setSpan(otelContext.active(), span), () => {
      fetch(`${apiBase}/api/client-error/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          service_name: serviceName,
          error_type: error.name,
          message: error.message,
          stack: error.stack || '',
          page_path: pagePath,
        }),
      }).catch(() => {})
      span.end()
    })
  } catch (_) {}
}

const B64 = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/'

function vlqDecode(str: string): number[] {
  const out: number[] = []
  let val = 0
  let shift = 0
  for (const c of str) {
    const d = B64.indexOf(c)
    if (d < 0) break
    val |= (d & 31) << shift
    shift += 5
    if (!(d & 32)) {
      out.push(val & 1 ? -(val >> 1) : val >> 1)
      val = 0
      shift = 0
    }
  }
  return out
}

interface ParsedFrame {
  fn: string
  file: string
  line: number
  col: number
}

function parseStack(stack: string): ParsedFrame[] {
  return (stack || '')
    .split('\n')
    .slice(1)
    .flatMap((l) => {
      const m =
        l.match(/^\s+at\s+(.+?)\s+\((.+?):(\d+):(\d+)\)/) ||
        l.match(/^\s+at\s+()(.+?):(\d+):(\d+)/)
      if (!m) return []
      return [
        { fn: m[1] || '<anonymous>', file: m[2], line: +m[3], col: +m[4] },
      ]
    })
}

interface RawSourceMap {
  sources: string[]
  mappings: string
}
const mapCache: Record<string, Promise<RawSourceMap | null>> = {}

function fetchMap(jsUrl: string): Promise<RawSourceMap | null> {
  if (!mapCache[jsUrl]) {
    mapCache[jsUrl] = fetch(jsUrl + '.map')
      .then((r) => (r.ok ? r.json() : null))
      .catch(() => null)
  }
  return mapCache[jsUrl]
}

function resolvePos(
  map: RawSourceMap,
  targetLine: number,
  targetCol: number,
): { src: string; line: number } | null {
  const lines = map.mappings.split(';')
  let srcIdx = 0
  let srcLine = 0
  let best: { src: string; line: number } | null = null
  for (let li = 0; li < lines.length; li++) {
    let col = 0
    for (const seg of lines[li].split(',')) {
      if (!seg) continue
      const f = vlqDecode(seg)
      col += f[0] ?? 0
      if (f.length >= 4) {
        srcIdx += f[1] ?? 0
        srcLine += f[2] ?? 0
        if (li + 1 === targetLine) {
          if (col > targetCol) return best
          const rawSrc = map.sources[srcIdx] ?? ''
          const src = rawSrc.replace(/^.*\/src\//, 'src/')
          best = { src, line: srcLine + 1 }
        }
      }
    }
    if (li + 1 === targetLine) return best
  }
  return best
}

async function resolveStack(err: Error): Promise<string> {
  const frames = parseStack(err.stack ?? '')
  const resolved = await Promise.all(
    frames.map(async (f) => {
      if (!f.file.includes('/assets/'))
        return `    at ${f.fn} (${f.file}:${f.line}:${f.col})`
      const map = await fetchMap(f.file)
      if (!map) return `    at ${f.fn} (${f.file}:${f.line}:${f.col})`
      const pos = resolvePos(map, f.line, f.col)
      if (!pos) return `    at ${f.fn} (${f.file}:${f.line}:${f.col})`
      return `    at ${f.fn} (${pos.src}:${pos.line})`
    }),
  )
  return `${err.name}: ${err.message}\n${resolved.join('\n')}`
}

export function captureError(error: unknown): void {
  const err = error instanceof Error ? error : new Error(String(error))
  resolveStack(err)
    .then((resolvedStack) => {
      const resolved = new Error(err.message)
      resolved.name = err.name
      resolved.stack = resolvedStack
      reportError(resolved, serviceName, getFullUrlPath(), getUserData())
    })
    .catch(() => {
      reportError(err, serviceName, getFullUrlPath(), getUserData())
    })
}

initOTel()
